import { Check } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

export const BUY_STEP_COUNT = 3;

/**
 * Steg-indikator for loddkjøp: 1 Velg lodd → 2 Dine opplysninger → 3 Betal.
 * `current` = aktivt steg (1–3); steg under `current` er ferdige. Under indikatoren står hjelpetekst for det aktive steget,
 * m.a. at valde nummer ikkje blir lagra for seg – dei blir haldne av først når kjøparen går til betaling.
 */
export const BuySteps = ({ current = 1, min = 1, className = "" }) => {
  const { t } = useLang();
  const steps = [
    {
      n: 1,
      label: t("Velg lodd"),
      hint: min > 1
        ? t("Trykk på minst {n} ledige nummer i rutenettet. Valget lagres ikke for seg – numrene holdes av for deg først når du går til betaling.", { n: min })
        : t("Trykk på ledige nummer i rutenettet. Valget lagres ikke for seg – numrene holdes av for deg først når du går til betaling."),
    },
    { n: 2, label: t("Dine opplysninger"), hint: t("Fyll inn navn og mobilnummer. E-post er valgfritt.") },
    { n: 3, label: t("Betal"), hint: t("Trykk «Betal med Vipps». Numrene reserveres i 15 minutter mens du betaler, og blir dine når betalingen er godkjent.") },
  ];
  const active = steps[Math.min(BUY_STEP_COUNT, Math.max(1, current)) - 1];

  return (
    <div className={`space-y-2.5 ${className}`} data-testid="basar-buy-steps" data-step={current}>
      <ol className="flex items-start" aria-label={t("Kjøpssteg")}>
        {steps.map((s, i) => {
          const done = s.n < current;
          const isActive = s.n === current;
          return (
            <li
              key={s.n}
              className="relative flex flex-1 flex-col items-center text-center"
              aria-current={isActive ? "step" : undefined}
              data-testid={`basar-buy-step-${s.n}`}
              data-state={done ? "done" : isActive ? "active" : "pending"}
            >
              {i < steps.length - 1 && (
                <span aria-hidden className={`absolute left-1/2 top-3.5 h-0.5 w-full ${done ? "bg-primary" : "bg-border"}`} />
              )}
              <span
                className={`relative z-10 inline-flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold tabular-nums transition-colors duration-150 ${
                  done
                    ? "border-primary bg-primary text-primary-foreground"
                    : isActive
                      ? "border-primary bg-background text-primary"
                      : "border-border bg-background text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" aria-hidden /> : s.n}
                {done && <span className="sr-only">{t("Fullført")}</span>}
              </span>
              <span className={`mt-1.5 text-[11px] leading-tight sm:text-xs ${isActive ? "font-semibold text-foreground" : done ? "text-foreground/80" : "text-muted-foreground"}`}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground" data-testid="basar-buy-step-hint" aria-live="polite">
        <span className="font-medium text-foreground">{t("Steg {n} av {total}", { n: current, total: BUY_STEP_COUNT })}: </span>
        {active.hint}
      </p>
    </div>
  );
};
